import { Platform } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Heading, Layout, Skeleton, Text } from '../../components/core';
import { IconMantraLogo } from '../../components/icons/IconMantraLogo';
import { ImageMantraHealth } from '../../components/images/ImageMantraHealth';
import { PageFooter } from '../../components/page';
import { useOnboardingUserDataQuery } from '../../graphQL';
import { usePushNotifications } from '../../plugins/pushNotifications/usePushNotifications';
import { getRoute } from '../../routes';
import { getStylesheet } from '../../styles';
import { useTitle } from '../../utils/useTitle';
import { OnboardingForm } from './forms/OnboardingForm';

export function OnboardingPage(): JSX.Element {
  useTitle('Welcome');

  const navigate = useNavigate();
  const { registerForPushNotifications } = usePushNotifications();
  const { data: onboardingUserData, loading: loadingOnboardingUserData } =
    useOnboardingUserDataQuery();

  const userData = onboardingUserData?.onboardingHubUser;
  const defaultUserName = userData?.preferredName ?? '';
  const birthDate = userData?.birthDate ?? '';
  const orgSelectOptions = userData?.organizationSelectOptions ?? [];

  const onSaved = async ({ skipConsents }: { skipConsents: boolean }): Promise<void> => {
    if (Platform.OS !== 'web' && skipConsents) {
      // If terms have been signed, then register now.
      await registerForPushNotifications();
    }

    const to = skipConsents
      ? getRoute('onboardingPhoneNumber', {})
      : getRoute('onboardingTerms', {});

    navigate(to);
  };

  const onConsentBlocked = (): void => {
    navigate(getRoute('home', {}));
  };

  return (
    <Layout.Flex {...styles.page} testID="onboarding-page">
      <Layout.HStack alignItems="center" {...styles.logo} space={3} marginBottom={8}>
        <IconMantraLogo aria-hidden size={5} />

        <ImageMantraHealth height={16} />
      </Layout.HStack>
      <Layout.VStack {...styles.stack} space={6}>
        <Layout.VStack space={0} alignItems="start">
          <Heading.h2>Welcome!</Heading.h2>
          <Heading.h2>Let's get started.</Heading.h2>
        </Layout.VStack>

        <Text.para>Please provide the following information to begin.</Text.para>

        {loadingOnboardingUserData ? (
          <Skeleton height={40} />
        ) : (
          <OnboardingForm
            defaultUserName={defaultUserName}
            currentBirthDate={birthDate}
            orgSelectOptions={orgSelectOptions}
            onConsentBlocked={onConsentBlocked}
            onSaved={onSaved}
          />
        )}
      </Layout.VStack>

      <PageFooter />
    </Layout.Flex>
  );
}

const styles = getStylesheet({
  page: {
    height: '100%',
    marginTop: 16,
    position: 'relative',
  },
  logo: {
    alignSelf: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  stack: {
    alignItems: 'start',
    display: 'flex',
  },
});
